import { AuthService } from '@/auth/services/auth.service';
import { NotificationService } from '@/notificaction/notification.service';
import { PhoneInputComponent } from '@/profile/components/phone-input/phone-input.component';
import { GlobalStore } from '@/store/app.store';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CountryNamePipe } from '@edt/pipes/translate-country-name.pipe';
import { CUBA_MOVIL_CODE } from '@edt/util';
import { StoreCustomer } from '@medusajs/types';
import { finalize } from 'rxjs';

@Component({
  selector: 'store-profile-form',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    PhoneInputComponent,
    MatProgressSpinner,
  ],
  templateUrl: './profile-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CountryNamePipe],
})
export class ProfileFormComponent implements AfterViewInit {
  #fb = inject(NonNullableFormBuilder);
  #globalStore = inject(GlobalStore);
  #authSrv = inject(AuthService);
  #notificationSrv = inject(NotificationService);
  authData = this.#globalStore.authUserData;
  protected frm = this.#fb.group({
    first_name: [
      '',
      [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
    ],
    last_name: ['', [Validators.required, Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.pattern(CUBA_MOVIL_CODE)]],
    country_code: ['', [Validators.required]],
  });
  protected loading = signal(false);

  constructor() {
    effect(() => {
      if (this.loading()) {
        this.first_name.disable();
        this.last_name.disable();
        this.phone.disable();
      } else {
        this.first_name.enable();
        this.last_name.enable();
        this.phone.enable();
      }
    });
  }

  ngAfterViewInit(): void {
    this.loading.set(true);

    //Disable readonly inputs
    this.country_code.disable();
    this.email.disable();

    if (this.authData()) {
      const user = this.authData() as StoreCustomer;
      this.first_name.setValue(user.first_name ?? '');
      this.last_name.setValue(user.last_name ?? '');
      this.email.setValue(user.email ?? '');
      this.phone.setValue(user.phone ?? '');
    }
    this.loading.set(false);
  }

  onSubmit() {
    this.frm.markAllAsTouched();

    if (this.frm.valid) {
      this.loading.set(true);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { country_code, email, ...rest } = this.frm.getRawValue();
      this.#authSrv
        .updateCustomer(rest)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: data => {
            this.#globalStore.setAuthUser(data.customer);
            this.frm.markAsPristine();
            this.#notificationSrv.showSuccess(
              'Actualización de usuario satisfactoria'
            );
          },
        });
    }
  }

  get first_name() {
    return this.frm.controls['first_name'];
  }
  get last_name() {
    return this.frm.controls['last_name'];
  }
  get email() {
    return this.frm.controls['email'];
  }
  get phone() {
    return this.frm.controls['phone'];
  }
  get country_code() {
    return this.frm.controls['country_code'];
  }
}
